import {
  useGetFormByAssignmentId,
  useGetFormDetail2,
  useGetFormalityRequestDetail,
} from "@/queries/formality-request";
import {
  useSaveDraftForm,
  useSaveForm,
  useUploadFormField,
  invalidateInternalFormQueries,
} from "@/queries/formality-request/intern-forms.api";
import type {
  Step,
  Field as FieldFormality,
} from "@/types/entities/formality-request";
import {
  createCountryDataFetcher,
  createDataByIdFetcher,
  createDataCenterFetcher,
  createInteroperabilityFetcher,
} from "@/workflow_api/services/nodes.api";
import {
  Avatar,
  AvatarFallback,
  Heading,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import {
  ExternalDataProvider,
  MultiStepForm,
  type FormValues,
  type Step as FormStep,
} from "@sdt/render-form-engine";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Renderify } from "renderify-js";
import { FormSuccessScreen } from "./form-success-screen";
import { applyDynamicDates } from "@/workflow/components/configurations/form/form-data-formatter";
import {
  transformFieldsForExpedient,
  shouldIncludeAutosaveField,
  stringifyMultiValueFields,
  serializeMultiValuePhoneFields,
} from "../utils/form-interns.utils";
import { useGetMe } from "@/queries/me";
import {
  getAllFields,
  prepopulateSteps,
  extractFileFields,
  removeFileFields,
} from "./form-interns.helpers";

export const FormInterns = () => {
  const { data: me, isLoading: isLoadingMe } = useGetMe();
  const { showToast } = useToast();

  // Get form
  const params = useParams<{ formId: string; procedureId: string }>();
  const { data, isFetching, isLoading } = useGetFormDetail2(
    params.formId ?? "",
    params.procedureId ?? "",
  );

  const userFormAssigmentId = useMemo(() => {
    const userFormAssignments = data?.user_form_assignments ?? [];
    if (userFormAssignments.length === 0) {
      return "";
    }
    const userId = userFormAssignments.find(
      (a) => a.user_id === me?.data.id,
    )?.id;
    return userId ?? "";
  }, [data?.user_form_assignments, me?.data.id]);

  const {
    data: formByAssignmentId,
    isLoading: isLoadingFormByAssignment,
    isError: isFormByAssignmentError,
    error: formByAssignmentError,
  } = useGetFormByAssignmentId(userFormAssigmentId, {
    skip404Redirect: true,
  });

  const isForbiddenFormByAssignment =
    !userFormAssigmentId ||
    (isFormByAssignmentError &&
      ((formByAssignmentError as { response?: { status?: number } })?.response
        ?.status === 403 ||
        (formByAssignmentError as { response?: { status?: number } })?.response
          ?.status === 404));

  const navigate = useNavigate();

  const allForm: Step[] = data?.steps ?? [];

  const allFields: FieldFormality[] = getAllFields(allForm);

  const filledFieldsFromFormData = useMemo(() => {
    const map = new Map<string, FieldFormality["filled_fields"]>();
    for (const step of data?.steps ?? []) {
      for (const section of step.sections ?? []) {
        for (const field of section.fields ?? []) {
          if ((field.filled_fields ?? []).length > 0) {
            map.set(field.name, field.filled_fields);
          }
        }
      }
    }
    return map;
  }, [data?.steps]);

  const fileFieldNames = useMemo(
    () =>
      new Set(allFields.filter((f) => f.type === "file").map((f) => f.name)),
    [allFields],
  );

  const { mutateAsync: uploadField, isPending: isUploadingFiles } =
    useUploadFormField({ skipInvalidation: true });

  // Save form
  const { mutateAsync } = useSaveForm({ skipInvalidation: true });
  const { mutateAsync: saveDraft } = useSaveDraftForm();

  const handleAutosave = useCallback(
    async (autosaveData: { assignmentId: string; fields: FormValues }) => {
      if (!autosaveData.assignmentId) return;
      try {
        const rawFields = autosaveData.fields;

        const dateFieldNames = new Set(
          allFields
            .filter((f) =>
              [
                "date",
                "datetime",
                "date_range",
                "datetime_range",
                "time",
              ].includes(f.type),
            )
            .map((f) => f.name),
        );

        const fields = removeFileFields(rawFields, fileFieldNames);

        let filteredFields = Object.fromEntries(
          Object.entries(fields).filter(([key, value]) =>
            shouldIncludeAutosaveField(key, value, dateFieldNames),
          ),
        );

        if (Object.keys(filteredFields).length === 0) return;

        filteredFields = stringifyMultiValueFields(filteredFields, allFields);
        filteredFields = serializeMultiValuePhoneFields(
          filteredFields,
          allFields,
        );

        await saveDraft({
          assignmentId: autosaveData.assignmentId,
          fields: filteredFields,
        });
      } catch {
        // silently ignore autosave errors
      }
    },
    [saveDraft, allFields, fileFieldNames],
  );

  const uploadAllFiles = async (
    assignmentId: string,
    filesByField: Record<string, File[]>,
  ) => {
    for (const [fieldName, files] of Object.entries(filesByField)) {
      const matchedField = allFields.find((f) => f.name === fieldName);

      if (!matchedField) {
        continue;
      }

      await uploadField({
        assignmentId,
        fieldId: matchedField.id,
        files,
      });
    }
  };

  const [isSubmitted, setIsSubmitted] = useState(false);

  const onSubmit = async (
    fields: Record<string, unknown>,
    assignmentId: string,
  ) => {
    try {
      const filesByField = extractFileFields(fields);
      if (Object.keys(filesByField).length > 0) {
        await uploadAllFiles(assignmentId, filesByField);
      }

      let fieldsToSubmit = removeFileFields(fields, fileFieldNames);
      fieldsToSubmit = stringifyMultiValueFields(fieldsToSubmit, allFields);
      fieldsToSubmit = serializeMultiValuePhoneFields(
        fieldsToSubmit,
        allFields,
      );

      await mutateAsync({
        assignmentId,
        fields: fieldsToSubmit as Record<string, string>,
        // is_final_submission: true,
      });
      setIsSubmitted(true);
      showToast({
        title: "Envío de Formulario",
        description: "Los datos del formulario han sido guardados exitosamente",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square" type="icon">
                <Icon type="fas" name="save" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    } catch (error) {
      console.error("Error al subir/guardar:", error);
      showToast({
        title: "Error",
        description:
          "Ocurrió un error al enviar el formulario. Intenta nuevamente.",
        options: { type: "error", duration: 5000 },
      });
    }
  };

  // Date fields autofilled from an expedient: variable -> type, to normalize
  // only those dates to the format the engine expects.
  const expedientDateFields = useMemo(() => {
    const map = new Map<string, "date" | "datetime">();
    for (const field of allFields) {
      // default_value / "datetime" exist at runtime (engine reads them) but
      // aren't in the local field type, so access them loosely.
      const type = field.type as string;
      if (type !== "date" && type !== "datetime") continue;
      const variable = (
        field as { default_value?: { variable?: unknown } | null }
      ).default_value?.variable;
      if (typeof variable === "string" && variable.trim().length > 0) {
        map.set(variable, type);
      }
    }
    return map;
  }, [allFields]);

  // Data center options
  const queryClient = useQueryClient();
  const fetchExternalData = useMemo(
    () => createDataCenterFetcher(queryClient, expedientDateFields),
    [queryClient, expedientDateFields],
  );

  const fetchInteroperability = useMemo(
    () => createInteroperabilityFetcher(queryClient /*showToast*/),
    [queryClient /* showToast*/],
  );

  const fetchCountryData = useMemo(
    () => createCountryDataFetcher(queryClient),
    [queryClient],
  );

  const fetchDataById = useMemo(() => {
    type FetchDataById = NonNullable<
      React.ComponentProps<typeof ExternalDataProvider>["fetchDataById"]
    >;
    const fetcher = createDataByIdFetcher(queryClient);
    const typedFetcher: FetchDataById = async (
      table: string,
      source: string = "SDT",
      id: string = "",
    ) => {
      const data = await fetcher(table, source, id);
      return data ?? { id };
    };

    return typedFetcher;
  }, [queryClient]);

  const prepopulatedSteps: Array<FormStep> = useMemo(() => {
    const sourceSteps =
      (
        formByAssignmentId?.data as unknown as
          | {
              form?: {
                steps?: FormStep[];
              };
            }
          | undefined
      )?.form?.steps ?? [];

    // Apply the critical transformation: convert expedient fields to select with database options
    const transformed = sourceSteps.map((step) => ({
      description: step.description ?? "",
      name: step.name ?? "",
      id: step.id ?? "",
      order: step.order ?? 0,
      conditions: null,
      created_at: "",
      updated_at: "",
      sections: (step.sections ?? []).map((section) => ({
        ...section,
        fields: transformFieldsForExpedient(section.fields ?? []),
      })),
    }));

    return prepopulateSteps(transformed, filledFieldsFromFormData);
  }, [formByAssignmentId?.data, filledFieldsFromFormData]);

  // Get Formality Request Detail to get the code
  const { data: formalityRequest } = useGetFormalityRequestDetail(
    params.procedureId ?? "",
  );

  const lastAssignment = useMemo(() => {
    const userFormAssignments = data?.user_form_assignments ?? [];
    if (userFormAssignments.length === 0) {
      return undefined;
    }
    const userAssignmentId = userFormAssignments.filter(
      (a) => a.user_id === me?.data.id,
    );
    return userAssignmentId.length > 0 ? userAssignmentId.at(-1) : undefined;
  }, [data?.user_form_assignments, me?.data.id]);

  const formEngineStatus =
    lastAssignment?.status === "IN_PROGRESS"
      ? "ASSIGNED"
      : (lastAssignment?.status ?? "ASSIGNED");

  if (isSubmitted) {
    return (
      <FormSuccessScreen
        requestId={formalityRequest?.code ?? ""}
        onNavigateBack={() => {
          invalidateInternalFormQueries(queryClient);
          navigate(`/tramite/${params.procedureId}`);
        }}
      />
    );
  }

  return (
    <Renderify
      condition={!isLoading && !isFetching && !isLoadingMe && !!data}
      elseShow={
        <div className="max-h-full h-full flex flex-col gap-6">
          <Heading variant="h3" className="mb-6">
            {data?.name}
          </Heading>
          <div className="flex flex-col items-center justify-center flex-1 gap-4">
            <Icon
              type="far"
              name="spinner-third"
              className="text-on-disabled animate-spin text-4xl"
            />
          </div>
        </div>
      }
    >
      <Renderify
        condition={!isLoadingFormByAssignment && !isLoadingMe}
        elseShow={
          <div className="max-h-full h-full flex flex-col gap-6 no-portal-padding">
            <Heading
              variant="h3"
              className="mb-6 px-4 tablet:px-4 laptop:px-6 desktop:px-8"
            >
              {data?.name}
            </Heading>
            <div className="flex flex-col items-center justify-center flex-1 gap-4">
              <Icon
                type="far"
                name="spinner-third"
                className="text-on-disabled animate-spin text-4xl"
              />
            </div>
          </div>
        }
      >
        <div className="max-h-full h-full flex flex-col gap-6 no-portal-padding">
          <Heading
            variant="h3"
            className="mb-6 px-4 tablet:px-4 laptop:px-6 desktop:px-8"
          >
            {data?.name}
          </Heading>
          {isForbiddenFormByAssignment ||
          (!data?.user_form_assignments?.length &&
            !data?.form_assignments?.length) ? (
            // El guard de useEffect se encarga de redirigir; esto es solo fallback
            <div className="flex flex-col items-center justify-center flex-1 gap-4">
              <Icon
                type="far"
                name="circle-exclamation"
                className="text-warning text-4xl"
              />
              <p className="text-body-quiet text-center">
                No tienes asignado este formulario
              </p>
            </div>
          ) : (
            <ExternalDataProvider
              fetchData={fetchExternalData}
              fetchInteroperability={fetchInteroperability}
              fetchDataById={fetchDataById}
              fetchCountryData={fetchCountryData}
            >
              <MultiStepForm
                assignmentId={lastAssignment?.id ?? ""}
                steps={applyDynamicDates(prepopulatedSteps)}
                status={formEngineStatus}
                isLoading={isLoading || isUploadingFiles}
                onAutosave={handleAutosave}
                handleSaveForm={(data: Record<string, unknown>) => {
                  onSubmit(
                    data.fields as Record<string, unknown>,
                    data?.assignmentId as string,
                  );
                  extractFileFields(data.fields as Record<string, unknown>);
                }}
                onToast={(t) => {
                  showToast(t);
                }}
                submittedAt={""}
              />
            </ExternalDataProvider>
          )}
        </div>
      </Renderify>
    </Renderify>
  );
};
